import { searchAdsIntelModule } from '@/rules/page-access'
import { maAuthenticationGuard, maAuthorizationGuard, maCountryGuard, maParamsQueryNormalizeGuard, maTaxonomyGuard } from '@/router/guards'
import { Param, Query, RouteName, TitleFont } from '@/base/constants/route'
import { PRODUCT_FAMILY, PRODUCTS } from '@/base/constants/authorization'

const searchAdsIntelRoutes = {
  path: 'search-ads-intelligence',
  name: RouteName.SEARCH_ADS_INTELLIGENCE,
  meta: {
    productFamily: PRODUCT_FAMILY.SA_INTEL,
    shortTitle: 'SEARCH ADS',
    icon: 'apple',
    parent: true,
    requiredProducts: [PRODUCTS.SA_INTEL_ENTERPRISE],
  },
  redirect: { name: RouteName.SEARCH_ADS_INTELLIGENCE_AD_PLACEMENT_SEARCH_RESULT_APP },
  component: () => import('@/layouts/Dashboard.vue'),
  beforeEnter: maAuthenticationGuard,
  children: [
    {
      path: 'ad-placements',
      name: RouteName.SEARCH_ADS_INTELLIGENCE_AD_PLACEMENT,
      meta: {
        title: 'AD PLACEMENTS',
        titleFont: TitleFont.HEADLINE,
      },
      redirect: { name: RouteName.SEARCH_ADS_INTELLIGENCE_AD_PLACEMENT_SEARCH_RESULTS },
      children: [

        {
          path: 'search-result-app/:trackId?',
          name: RouteName.SEARCH_ADS_INTELLIGENCE_AD_PLACEMENT_SEARCH_RESULT_APP,
          meta: {
            title: 'Search Result/App',
            analyticsPageName: 'SA Intel - Search Result/App',
            productFamily: PRODUCT_FAMILY.SA_INTEL,
            requiredParams: [Param.TRACK_ID],
            disabledAutoScroll: true,
            requiredQueries: [
              Query.COUNTRY_FILTER,
              Query.COUNTRY_CODE,
              Query.START_DATE,
              Query.END_DATE,
              Query.ACTIVE_APP,
            ],
            requiredAccess: searchAdsIntelModule.searchByAppAccessRule,
          },
          component: () => import('@/pages/searchads-intelligence/search-by-app/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
        {
          path: 'search-result-keyword/:trackId?',
          name: RouteName.SEARCH_ADS_INTELLIGENCE_AD_PLACEMENT_SEARCH_RESULT_KEYWORD,
          meta: {
            title: 'Search Result/Keyword',
            analyticsPageName: 'SA Intel - Search Result/Keyword',
            productFamily: PRODUCT_FAMILY.SA_INTEL,
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.COUNTRY_FILTER, Query.COUNTRY_CODE, Query.KEYWORD, Query.START_DATE, Query.END_DATE],
            requiredAccess: searchAdsIntelModule.searchByKeywordAccessRule,
          },
          component: () =>
            import('@/pages/searchads-intelligence/search-by-keyword/SearchByKeyword.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
        {
          path: 'search-result-competitor/:trackId?',
          name: RouteName.SEARCH_ADS_INTELLIGENCE_AD_PLACEMENT_SEARCH_RESULT_COMPETITOR,
          meta: {
            title: 'Search Result/Competitor',
            analyticsPageName: 'SA Intel - Search Result/Competitor',
            productFamily: PRODUCT_FAMILY.SA_INTEL,
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.COUNTRY_FILTER, Query.COUNTRY_CODE, Query.START_DATE, Query.END_DATE],
            requiredAccess: searchAdsIntelModule.searchByCompetitorAccessRule,
          },
          component: () => import('@/pages/searchads-intelligence/SearchByCompetitor/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
        {
          path: 'today-tab',
          name: RouteName.SEARCH_ADS_INTELLIGENCE_AD_PLACEMENT_SEARCH_RESULTS_TODAY_TAB,
          meta: {
            title: 'Today Tab',
            analyticsPageName: 'SA Intel - Today Tab',
            productFamily: PRODUCT_FAMILY.SA_INTEL,
            navigationWithoutAppTrack: true,
            requiredQueries: [Query.COUNTRY_FILTER, Query.DATE],
            requiredAccess: searchAdsIntelModule.todayTabAccessRule,
          },
          component: () => import('@/pages/searchads-intelligence/TodayTab/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
        {
          path: 'search-tab',
          name: RouteName.SEARCH_ADS_INTELLIGENCE_AD_PLACEMENT_SEARCH_RESULTS_SEARCH_TAB,
          meta: {
            title: 'Search Tab',
            analyticsPageName: 'SA Intel - Search Tab',
            productFamily: PRODUCT_FAMILY.SA_INTEL,
            navigationWithoutAppTrack: true,
            requiredQueries: [Query.COUNTRY_FILTER, Query.DATE],
            requiredAccess: searchAdsIntelModule.searchTabAccessRule,
          },
          component: () => import('@/pages/searchads-intelligence/SearchTab/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
        {
          path: 'product-pages',
          name: RouteName.SEARCH_ADS_INTELLIGENCE_AD_PLACEMENT_SEARCH_RESULTS_PRODUCT_PAGES,
          redirect: { name: RouteName.SEARCH_ADS_INTELLIGENCE_AD_PLACEMENT_SEARCH_RESULTS_PRODUCT_PAGES_APPS_ON_MY_PAGE_TAB },
          meta: {
            title: 'Product Pages',
            analyticsPageName: 'SA Intel - Product Pages',
            productFamily: PRODUCT_FAMILY.SA_INTEL,
            ignoreChildrenFromMenu: true,
            requiredAccess: searchAdsIntelModule.productPagesAccessRule,
          },
          component: () => import('@/pages/searchads-intelligence/ProductPages/index.vue'),
          beforeEnter: maAuthorizationGuard,
          children: [
            {
              path: 'apps-on-my-page/:trackId?',
              name: RouteName.SEARCH_ADS_INTELLIGENCE_AD_PLACEMENT_SEARCH_RESULTS_PRODUCT_PAGES_APPS_ON_MY_PAGE_TAB,
              meta: {
                title: 'Product Pages',
                analyticsPageName: 'SA Intel - Product Pages - Apps On My Page',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [Query.DATE],
              },
              components: {
                tab: () => import('@/pages/searchads-intelligence/ProductPages/components/AppsOnMyPageTab.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
            },
            {
              path: 'apps-advertised-on/:trackId?',
              name: RouteName.SEARCH_ADS_INTELLIGENCE_AD_PLACEMENT_SEARCH_RESULTS_PRODUCT_PAGES_APPS_ADVERTISED_ON_TAB,
              meta: {
                title: 'Product Pages',
                analyticsPageName: 'SA Intel - Product Pages - Apps Advertised On',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [Query.DATE],
              },
              components: {
                tab: () => import('@/pages/searchads-intelligence/ProductPages/components/AppsAdvertisedOnTab.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
            },
          ],
        },
      ],
    },
    {
      path: 'top-charts',
      name: RouteName.SEARCH_ADS_INTELLIGENCE_TOP_CHARTS,
      meta: {
        title: 'TOP CHARTS',
        titleFont: TitleFont.HEADLINE,
      },
      redirect: { name: RouteName.SEARCH_ADS_INTELLIGENCE_TOP_CHARTS },
      children: [
        {
          path: 'top-advertisers',
          name: RouteName.SEARCH_ADS_INTELLIGENCE_TOP_CHARTS_TOP_ADVERTISERS,
          meta: {
            title: 'Top Advertisers',
            analyticsPageName: 'SA Intel - Top Advertisers',
            productFamily: PRODUCT_FAMILY.SA_INTEL,
            navigationWithoutAppTrack: true,
            requiredQueries: [Query.COUNTRY_FILTER, Query.DATE],
            requiredAccess: searchAdsIntelModule.topAdvertisersAccessRule,
          },
          component: () => import('@/pages/searchads-intelligence/top-advertisers/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maTaxonomyGuard, maCountryGuard],
        },
      ],
    },
    {
      path: 'custom-product-pages',
      name: RouteName.SEARCH_ADS_INTELLIGENCE_CUSTOM_PRODUCT_PAGES,
      meta: {
        title: 'CUSTOM PRODUCT PAGES',
        titleFont: TitleFont.HEADLINE,
      },
      redirect: { name: RouteName.SEARCH_ADS_INTELLIGENCE_CUSTOM_PRODUCT_PAGES_CPP_INTELLIGENCE },
      children: [
        {
          path: 'cpp-intelligence',
          name: RouteName.SEARCH_ADS_INTELLIGENCE_CUSTOM_PRODUCT_PAGES_CPP_INTELLIGENCE,
          meta: {
            title: 'CPP Intelligence',

            analyticsPageName: 'SA Intel - CPP Intelligence',
            productFamily: PRODUCT_FAMILY.CPP,
            ignoreChildrenFromMenu: true,
            requiredAccess: searchAdsIntelModule.cppIntelligenceAccessRule,
          },
          redirect: {
            name: RouteName.SEARCH_ADS_INTELLIGENCE_CUSTOM_PRODUCT_PAGES_CPP_INTELLIGENCE_CPP_BY_APP,
          },
          beforeEnter: maAuthorizationGuard,
          children: [
            {
              path: 'cpp-by-app/:trackId?',
              name: RouteName.SEARCH_ADS_INTELLIGENCE_CUSTOM_PRODUCT_PAGES_CPP_INTELLIGENCE_CPP_BY_APP,
              meta: {
                title: 'CPP Intelligence',
                analyticsPageName: 'SA Intel - CPP Intelligence - CPP By App',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [
                  Query.ACTIVE_APP,
                  Query.COUNTRY_FILTER,
                  Query.COUNTRY_CODE,
                  Query.START_DATE,
                  Query.END_DATE,
                ],
              },
              components: {
                tab: () =>
                  import(
                    '@/pages/searchads-intelligence/CustomProductPageIntelligence/components/MaCppByApp.vue'
                  ),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
            },
            {
              path: 'cpp-by-keyword/:trackId?',
              name: RouteName.SEARCH_ADS_INTELLIGENCE_CUSTOM_PRODUCT_PAGES_CPP_INTELLIGENCE_CPP_BY_KEYWORD,
              meta: {
                title: 'CPP Intelligence',
                analyticsPageName: 'SA Intel - CPP Intelligence - CPP By Keyword',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [
                  Query.COUNTRY_FILTER,
                  Query.COUNTRY_CODE,
                  Query.KEYWORD,
                  Query.START_DATE,
                  Query.END_DATE,
                  Query.CPP_FILTER,
                ],
              },
              components: {
                tab: () =>
                  import(
                    '@/pages/searchads-intelligence/CustomProductPageIntelligence/components/MaCppByKeyword.vue'
                  ),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
            },
            {
              path: 'cpp-by-category/:trackId?',
              name: RouteName.SEARCH_ADS_INTELLIGENCE_CUSTOM_PRODUCT_PAGES_CPP_INTELLIGENCE_CPP_BY_CATEGORY,
              meta: {
                title: 'CPP Intelligence',
                analyticsPageName: 'SA Intel - CPP Intelligence - CPP By Category',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [
                  Query.COUNTRY_FILTER,
                  Query.COUNTRY_CODE,
                  Query.START_DATE,
                  Query.END_DATE,
                ],
              },
              components: {
                tab: () =>
                  import(
                    '@/pages/searchads-intelligence/CustomProductPageIntelligence/components/MaCppByCategory.vue'
                  ),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maTaxonomyGuard, maCountryGuard],
            },
            {
              path: 'cpp-by-ad-netword/:trackId?',
              name: RouteName.SEARCH_ADS_INTELLIGENCE_CUSTOM_PRODUCT_PAGES_CPP_INTELLIGENCE_CPP_BY_AD_NETWORK,
              meta: {
                title: 'CPP Intelligence',
                analyticsPageName: 'SA Intel - CPP Intelligence - CPP on Ad Networks',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [
                  Query.START_DATE,
                  Query.END_DATE,
                  Query.AD_NETWORK,
                  Query.COUNTRY_FILTER,
                  Query.COUNTRY_CODE,
                ],
              },
              components: {
                tab: () =>
                  import(
                    '@/pages/searchads-intelligence/CustomProductPageIntelligence/cpp-by-ad-network/index.vue'
                  ),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maTaxonomyGuard, maCountryGuard],
            },
          ],
          component: () => import('@/pages/searchads-intelligence/CustomProductPageIntelligence/index.vue'),
        },
        {
          path: 'cpp-metrics/:trackId?',
          name: RouteName.SEARCH_ADS_INTELLIGENCE_CUSTOM_PRODUCT_PAGES_CPP_METRICS,
          meta: {
            title: 'CPP Metrics',
            analyticsPageName: 'SA Intel - CPP Metrics',
            productFamily: PRODUCT_FAMILY.SA_INTEL,
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.COUNTRY_CODE, Query.DEVICE, Query.START_DATE, Query.END_DATE],
            requiredAccess: searchAdsIntelModule.cppMetricsAccessRule,
          },
          component: () => import('@/pages/searchads-intelligence/CustomProductPageMetrics/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
      ],
    },
  ],
}

export default searchAdsIntelRoutes
